import Debug from 'debug'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { apiPathToRoute } from '../FileRoute/utils/apiPathToRoute'
import { useParsedFileRouteParams } from '../FileRoute/utils/parseFileRouteParams'
import { useFileStore } from '../FileStore/FileContext'
import type { FolderStore } from '../Folder/FolderStore'
import { CreateFolderDialog } from './CreateFolderDialog/CreateFolderDialog'
import { DeleteDeviceDialog } from './DeleteDeviceDialog/DeleteDeviceDialog'
import { EmptyTrashDialog } from './EmptyTrashDialog/EmptyTrashDialog'
import { FileRevisionsDialog } from './FileRevisionsDialog/FileRevisionsDialog'
import { FileTransferDialog } from './FileTransferDialog/FileTransferDialog'
import { PermanentlyDeleteDialog } from './PermanentlyDeleteDialog/PermanentlyDeleteDialog'
import { ShareFileDialog } from './ShareFileDialog/ShareFileDialog'
import type { FileActionContext } from '@jotta/types/FileActions'
import { useBrandName } from '@jotta/grpc-connect-client/config'
import {
  useEmptyTrashMutation,
  useFileTrashMutation,
} from '@jotta/grpc-connect-client/file/useTrash'
import { useDeleteDeviceMutation } from '@jotta/grpc-connect-client/device/useDevice'
import { t } from '@lingui/macro'
import { dismissToast, showToast } from '@jotta/ui/ToastProvider'
import { getToastContent } from '../../../ui/src/Toasts/backgroundJobToasts'
import {
  removeBackgroundJobsByActionId,
  removeBackgroundJobsByType,
} from '@jotta/hooks/useBackgroundJobStore'
import { nanoid } from 'nanoid'
const debug = Debug('jotta:files:FolderAction')

export interface DisplayActiveFileActionDialogProps {
  store: FolderStore
  /** Set to true when component is rendered inside a carousel or other modal view */
  isNestedModal?: boolean
}
export const DisplayActiveFileActionDialog =
  observer<DisplayActiveFileActionDialogProps>(
    function DisplayActiveFileActionDialog({ store, isNestedModal }) {
      const brandName = useBrandName()
      const navigate = useNavigate()
      const { actions } = store
      const { dispatch } = actions
      const a = actions.activeFileActionData
      const { currentRouteIsFile } = useFileStore()
      const { apiPath } = useParsedFileRouteParams()
      const [searchParams, setSearchParams] = useSearchParams()
      const actionSearchParam = searchParams.get('action')
      const shareActionContext: FileActionContext | undefined =
        actionSearchParam === 'share' && a.type === 'Disabled'
          ? currentRouteIsFile
            ? 'FOLDER_VIEW'
            : 'FOLDER_LIST'
          : undefined
      const context = store.route.context

      const deleteDeviceMutation = useDeleteDeviceMutation({
        onMutate: () => {
          actions.cancelActiveAction()
          store.selection.selectNone()
          return showToast({
            ...getToastContent('DELETE_DEVICE', 'IN_PROGRESS'),
            id: nanoid(),
          })
        },
        onSettled: (status, toastId) => {
          store.refresh()
          showToast({
            ...getToastContent('DELETE_DEVICE', status),
            id: toastId,
            onDismiss: () =>
              toastId ? removeBackgroundJobsByActionId(toastId) : undefined,
          })
        },
      })
      const emptyTrashMutation = useEmptyTrashMutation({
        onMutate: variables => {
          navigate('/web/trash', { replace: true }) // To avoid encountering "not found" error when emptying trash from within a subfolder
          actions.cancelActiveAction()
          const toastId = nanoid()

          showToast({
            ...getToastContent('EMPTY_TRASH', 'IN_PROGRESS'),
            id: toastId,
            actionButton: {
              label: t`Cancel`,
              onClick: () => {
                variables.isCancelled = true
                removeBackgroundJobsByType('DELETE_PERMANENTLY')
                dismissToast(toastId)
              },
            },
          })
          return toastId
        },
        onSettled: (status, actionId, variables) => {
          store.refresh()
          if (variables.isCancelled) {
            return
          }
          showToast({
            ...getToastContent('EMPTY_TRASH', status),
            id: actionId,
          })
        },
      })
      const fileTrashMutation = useFileTrashMutation({
        onMutate: variables => {
          actions.cancelActiveAction()
          store.selection.selectNone()
          const toastId = variables.toastId ?? nanoid()
          return showToast({
            ...getToastContent(variables.actionType, 'IN_PROGRESS'),
            id: toastId,
          })
        },
        onSettled: (_data, error, variables, toastId) => {
          store.refresh()
          const status = error ? 'FAILED' : 'SUCCESS'
          showToast({
            ...getToastContent(variables.actionType, status),
            id: toastId,
            onDismiss: () =>
              toastId ? removeBackgroundJobsByActionId(toastId) : undefined,
            actionButton:
              variables.actionType === 'MOVE_TO_TRASH' && status === 'SUCCESS'
                ? {
                    label: t`Undo`,
                    onClick: () => {
                      fileTrashMutation.mutate({
                        actionType: 'RESTORE',
                        paths: variables.paths,
                        toastId: toastId,
                      })
                    },
                  }
                : undefined,
          })
        },
      })

      useEffect(() => {
        if (shareActionContext) {
          dispatch('Share', shareActionContext, apiPath)
          setSearchParams(
            searchParams => {
              searchParams.delete('action')
              return searchParams
            },
            {
              replace: true,
            },
          )
        }
      }, [apiPath, dispatch, setSearchParams, shareActionContext])

      useEffect(() => {
        if (a.type === 'Disabled') {
          return
        }
        const item = store.getItemByPath(a.path)
        if (a.type === 'AcceptPendingInvite') {
          actions.acceptPendingInvite(a.path)
          return
        }
        if (a.type === 'DeclinePendingInvite') {
          actions.declinePendingInvite(a.path)
          return
        }
        if (!item) {
          return
        }
        const isSelection = a.context === 'SELECTION'
        const items = isSelection ? store.selectedChildren : [item]
        const paths = items.map(item => item.path)
        switch (a.type) {
          case 'OpenDocument':
          case 'OpenExcelDocument':
          case 'OpenWordDocument':
          case 'OpenPowerpointDocument': {
            const action = item.data.size === 0 ? 'editnew' : 'edit'
            const routePath = `${apiPathToRoute(a.path, {
              context,
            })}?action=${action}`
            debug('Navigate to %s', routePath, a.path)
            navigate(routePath)
            setTimeout(() => {
              actions.cancelActiveAction()
            }, 0)
            break
          }
          case 'Delete': {
            fileTrashMutation.mutate({
              actionType: 'MOVE_TO_TRASH',
              paths,
            })
            break
          }
          case 'Download': {
            ;(isSelection
              ? actions.downloadSelectedItems(brandName || item.name, items)
              : actions.downloadItem()
            )
              .catch(reason => {
                reportError(reason)
                showToast({
                  message: t`An error occured!`,
                  hasCloseButton: true,
                  duration: Infinity,
                })
              })
              .finally(() => actions.cancelActiveAction())
            break
          }
          case 'Restore': {
            fileTrashMutation.mutate({
              actionType: 'RESTORE',
              paths,
            })
            break
          }
          case 'NewExcelDocument':
            actions.createDocument({ path: a.path, documentType: 'xlsx' })
            break
          case 'NewWordDocument':
            actions.createDocument({ path: a.path, documentType: 'docx' })
            break
          case 'NewPowerpointDocument':
            actions.createDocument({ path: a.path, documentType: 'pptx' })
            break

          default:
            break
        }
      }, [a, navigate, store, actions, context])

      if (a.type === 'Disabled') {
        return null
      }

      switch (a.type) {
        case 'Copy':
          return (
            <FileTransferDialog
              error={a.error}
              clearError={actions.clearActiveFileActionError}
              transferType="COPY"
              filePaths={
                a.context === 'SELECTION'
                  ? store.selectedChildren.map(f => f.path)
                  : [a.path]
              }
              open={true}
              nested={isNestedModal}
              defaultOpen={true}
              transferFn={actions.transferItems}
              variant={'dialogs.overlay'}
              onClose={actions.cancelActiveAction}
              onFolderCreated={store.refresh}
            />
          )
        case 'CreateFolder': {
          return (
            <CreateFolderDialog
              createFolder={actions.createFolder}
              error={a.error}
              clearError={actions.clearActiveFileActionError}
              open={true}
              nested={isNestedModal}
              defaultOpen={true}
              variant={'dialogs.overlay'}
              onClose={actions.cancelActiveAction}
            />
          )
        }
        case 'DeletePermanently': {
          return (
            <PermanentlyDeleteDialog
              paths={
                a.context === 'SELECTION'
                  ? store.selectedChildren.map(f => f.path)
                  : [a.path]
              }
              onConfirm={paths => {
                fileTrashMutation.mutate({
                  actionType: 'DELETE_PERMANENTLY',
                  paths,
                })
              }}
              open={true}
              nested={isNestedModal}
              defaultOpen={true}
              variant={'dialogs.overlay'}
              onClose={actions.cancelActiveAction}
            />
          )
        }
        case 'DeleteDevice': {
          const item =
            store.selectedChildren.length === 1
              ? store.selectedChildren[0]
              : store.children.find(item => item.path === a.path)

          if (!item || !item.isDevice) {
            return
          }

          const deviceId = item.path.split('/').slice(-1)[0]

          return (
            <DeleteDeviceDialog
              onConfirm={() => {
                deleteDeviceMutation.mutate({
                  id: deviceId,
                  supportsBackgroundJobs: true,
                })
              }}
              deviceName={item.name}
              open={true}
              nested={isNestedModal}
              onClose={actions.cancelActiveAction}
              variant={'dialogs.overlay'}
            />
          )
        }
        case 'EmptyTrash':
          return (
            <EmptyTrashDialog
              onConfirm={() =>
                emptyTrashMutation.mutate({ isCancelled: false })
              }
              open={true}
              nested={isNestedModal}
              defaultOpen={true}
              variant={'dialogs.overlay'}
              onClose={actions.cancelActiveAction}
            />
          )
        case 'Move':
          return (
            <FileTransferDialog
              error={a.error}
              clearError={actions.clearActiveFileActionError}
              transferType="MOVE"
              filePaths={
                a.context === 'SELECTION'
                  ? store.selectedChildren.map(f => f.path)
                  : [a.path]
              }
              open={true}
              nested={isNestedModal}
              defaultOpen={true}
              transferFn={actions.transferItems}
              variant={'dialogs.overlay'}
              onClose={actions.cancelActiveAction}
            />
          )
        case 'Revisions':
          return (
            <FileRevisionsDialog
              path={a.path}
              open={true}
              nested={isNestedModal}
              defaultOpen={true}
              variant={'dialogs.overlay'}
              onClose={actions.cancelActiveAction}
            />
          )
        case 'Share': {
          const item = store.getItemByPath(a.path)
          if (!item) {
            return
          }
          return (
            <ShareFileDialog
              item={item}
              open={true}
              nested={isNestedModal}
              defaultOpen={true}
              variant={'dialogs.overlay'}
              onClose={actions.cancelActiveAction}
              onLeaveShare={store.actions.leaveShare}
              context={context}
            />
          )
        }
        default:
          return null
      }
    },
  )
